import produce from 'immer';
import * as _ from 'lodash';

import { ProjectCategory } from '../../../shared/projectsV2/common';
import { COUNT_MAX_FE } from '../../../shared/projectsV2/request';
import { isValidCategory, isValidSort, parseQueryParams } from '../../helpers/projectsV2';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { BulkAddLabelResponse, LabelsResponse } from '../../../shared/labels';
import type { AnyFilter } from '../../../shared/projectsV2/filters';
import type {
  ProjectsResponse,
  ProjectsSummaryResponse,
  ReleaseGroupsResponse,
} from '../../../shared/projectsV2/response';
import type { AnySort } from '../../../shared/projectsV2/sorting';
import type { ReduxAction, ReduxActionResponse, SyncReduxAction } from '../../types';

import { actionTypes } from './actions';
import { DEFAULT_COUNT, DEFAULT_PAGE, DEFAULT_SORT, DEFAULT_TITLE } from './consts';
import { areAllProjectsIndividuallySelected, areAllProjectsSelected, isAnyProjectOnPageSelected } from './utils';
import type { HydrateStateQuery, ProjectsV2Store } from './types';

export const initialState: Readonly<ProjectsV2Store> = Object.freeze({
  /**
   * AsyncStatus, tracks the network status as well as any error messages.
   */
  asyncStatus: {
    fetchProjects: getInitialStatus(),
    fetchCategoriesSummary: getInitialStatus(),
    fetchOrganzationLabels: getInitialStatus(),
    updateProjects: getInitialStatus(),
    removeTeam: getInitialStatus(),
    bulkApplyLabel: getInitialStatus(),
  },

  /**
   * This contains data about the response when applying a label to many projects.
   * It contains a number representing the number of projects the label was applied to and the error map for
   * messages when label application failed.
   */
  bulkLabelData: {
    created: 0,
    errors: {},
  },

  /**
   * category refers to the "category" of projects we are looking at,
   * either "projects" or "release groups".
   */
  category: ProjectCategory.Projects,

  /**
   * This is the counts for projects and release groups.
   * If this field is null, it is because they have not been fetched yet.
   * Categories summary is only fetched once on initial mount.
   * @example:
   * {
   *   projects: 123,
   *   releaseGroups: 4,
   * },
   */
  categoriesSummary: null,

  /**
   * This field allows us to track which projects are selected.
   */
  selectedProjects: [],

  /**
   * Projects represents the VISIBLE projects, ie: the current "page" of projects with filters applied.
   */
  projects: [],

  /**
   * Number of projects that match the current selection criteria (used for pagination).
   */
  totalProjects: 0,

  /**
   * Labels available to the organization.
   */
  organizationLabels: [],

  /**
   * Searchbar's search query (search by title)
   */
  title: DEFAULT_TITLE,

  /**
   * By default, sort by the projects' latest scan (in descending order)
   */
  sort: DEFAULT_SORT,

  /**
   * Default to no filters being applied.
   */
  filter: {},

  /**
   * Pagination information for when making requests.
   */
  page: DEFAULT_PAGE,
  count: DEFAULT_COUNT,
});

/**
 * Helper functions for resetting state, usually
 * after network requests happen. These help make the
 * intent clear in the reducers below.
 */

// Pagination resets after every request.
const resetPage = (draftState: ProjectsV2Store) => {
  draftState.page = initialState.page;
};

const resetSelectedProjects = (draftState: ProjectsV2Store) => {
  draftState.selectedProjects = initialState.selectedProjects;
};

// These two fields often need to be reset together, whenever a filter/param changes and we re-fetch projects.
const resetPageAndSelectedProjects = (draftState: ProjectsV2Store) => {
  resetPage(draftState);
  resetSelectedProjects(draftState);
};

// Reducer for the RESET state action.
const resetStateReducer = {
  [actionTypes.RESET]: (state: ProjectsV2Store, action: SyncReduxAction<{ keepCategory? : boolean }>)
    : ProjectsV2Store => {
    if (action.keepCategory) {
      return { ...initialState, category: state.category };
    }
    return initialState;
  },
};

const preFetchLoadingReducer = {
  [actionTypes.PRE_FETCH_LOADING]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchProjects = getPendingStatus();
    }),
};

// Reducer for the SET_SORT action.
const setSortReducer = {
  [actionTypes.SET_SORT]: (state: ProjectsV2Store, action: SyncReduxAction<{ sort: AnySort }>) =>
    produce(state, draftState => {
      resetPageAndSelectedProjects(draftState);
      draftState.sort = action.sort;
    }),
};

const setTitleReducer = {
  [actionTypes.SET_TITLE]: (state: ProjectsV2Store, action: SyncReduxAction<{ title: string }>) =>
    produce(state, draftState => {
      resetPageAndSelectedProjects(draftState);
      draftState.title = action.title || '';
    }),
};

const paginationReducer = {
  [actionTypes.SET_PAGE]: (state: ProjectsV2Store, action: SyncReduxAction<{ page: number }>) =>
    produce(state, draftState => {
      draftState.page = action.page;
    }),
  [actionTypes.SET_COUNT]: (state: ProjectsV2Store, action: SyncReduxAction<{ count: number }>) =>
    produce(state, draftState => {
      draftState.count = action.count;
      // Reset the page to 1 when the count changes, or else they will be in a weird state.
      // We also blow away their selected projects for the same reason.
      resetPageAndSelectedProjects(draftState);
    }),
};

const hydrateStateReducer = {
  [actionTypes.HYDRATE_STATE]: (state: ProjectsV2Store, action: SyncReduxAction<{ query: HydrateStateQuery }>) =>
    produce(state, draftState => {
      // Ensure that any query params are parsed into primatives (see parseQueryParams for more info).
      const query = parseQueryParams(action.query);

      if (query.category && isValidCategory(query.category)) {
        // Reset selected projects if the new category isn't the same as the state category
        if (query.category !== draftState.category) {
          resetPageAndSelectedProjects(draftState);
        }
        draftState.category = action.query.category;
      }

      if (query.sort && isValidSort(draftState.category, query.sort)) {
        draftState.sort = query.sort;
      } else {
        draftState.sort = initialState.sort;
      }

      if (query.page && Number.isFinite(query.page)) {
        draftState.page = query.page;
      } else {
        resetPage(draftState);
      }

      if (query.count) {
        const numericCount = query.count;
        //  If it's NaN/some garbage, we just ignore and don't set the count.
        if (Number.isFinite(numericCount)) {
          draftState.count = numericCount > COUNT_MAX_FE
            ? COUNT_MAX_FE
            : numericCount;
        }
      }

      draftState.title = query.title || '';
      draftState.filter = query.filter ? query.filter : {};
    }),
};

// Reducers for FETCH_PROJECTS actions.
const fetchProjectsReducers = {
  [`${actionTypes.FETCH_PROJECTS}_PENDING`]: (state: ProjectsV2Store, action: ReduxActionResponse<ProjectsResponse | ReleaseGroupsResponse>) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchProjects = getPendingStatus();
      draftState.asyncStatus.fetchProjects.timestamp = action.meta.timestamp;
      draftState.projects = initialState.projects;
    }),
  [`${actionTypes.FETCH_PROJECTS}_FULFILLED`]: (state: ProjectsV2Store, action: ReduxActionResponse<ProjectsResponse | ReleaseGroupsResponse>) =>
    produce(state, draftState => {
      if (draftState.asyncStatus.fetchProjects.timestamp === action.meta.timestamp) {
        draftState.asyncStatus.fetchProjects = getInitialStatus();
        if (action.payload.data?.projects) {
          draftState.projects = action.payload.data?.projects;
        } else if (action.payload.data?.releaseGroups) {
          draftState.projects = action.payload.data?.releaseGroups;
        } else {
          draftState.projects = [];
        }
        draftState.totalProjects = action.payload.data?.total ?? 0;
      }
    }),
  [`${actionTypes.FETCH_PROJECTS}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchProjects = getErrorStatus(action.payload);
    }),
};

const selectProjectsReducer = {
  [actionTypes.SELECT_PROJECT]: (state: ProjectsV2Store, action: SyncReduxAction<{ projectId: string }>) =>
    produce(state, draftState => {
      //  Check that the project we are selecting is not already in the selectedProjects list.
      //  This would probably never happen but is a safeguard to avoid a weird state.
      if (
        !areAllProjectsSelected(draftState.selectedProjects) &&
        !draftState.selectedProjects.some((project) => String(project.id) === action.projectId)
      ) {
        draftState.selectedProjects =
          [...draftState.selectedProjects,
            draftState.projects.find(({ id }) => String(id) === action.projectId)];
      }
      //  This would only ever happen if a user went to every page and selected each item,
      //  so it's unlikely, but we still need to handle it correctly so the banned will say "everything selected".
      if (areAllProjectsIndividuallySelected(draftState)) {
        draftState.selectedProjects = 'all';
      }
    }),
  [actionTypes.DESELECT_PROJECT]: (state: ProjectsV2Store, action: SyncReduxAction<{ projectId: string }>) =>
    produce(state, draftState => {
      //  If everything was selected and they deselect one project, the selected projects becomes
      //  just the projects on the current page minus the one which was deselected.
      if (areAllProjectsSelected(draftState.selectedProjects)) {
        draftState.selectedProjects = [...draftState.projects];
      }
      //  Now we explicitly remove the project we just selected.
      draftState.selectedProjects = draftState.selectedProjects.filter(({ id }) => String(id) !== action.projectId);
    }),
  [actionTypes.TOGGLE_SELECT_ALL_ON_PAGE]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      //  If all the projects are selected, then deselect everything.
      if (areAllProjectsSelected(draftState.selectedProjects)) {
        draftState.selectedProjects = initialState.selectedProjects;
        //  If any of the projects on the current page are selected, deselect them (leaving any selected projects from other pages).
      } else if (isAnyProjectOnPageSelected(draftState.projects, draftState.selectedProjects)) {
        draftState.selectedProjects = draftState.selectedProjects
          .filter(project => !(draftState.projects).find(({ id }) => String(id) === project.id));
      } else if (draftState.totalProjects ===
        (draftState.selectedProjects.length + draftState.projects.length)) {
        // All projects are selected
        draftState.selectedProjects = 'all';
      } else {
        //  Otherwise select everything!
        draftState.selectedProjects = _.uniqBy([
          ...draftState.selectedProjects,
          ...draftState.projects,
        ], 'id');
      }
    }),
  [actionTypes.SELECT_ALL_PROJECTS]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.selectedProjects = 'all';
    }),
  [actionTypes.CLEAR_SELECTION]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.selectedProjects = initialState.selectedProjects;
    }),
};

//  Reducers for FETCH_PROJECTS_SUMMARY actions
const fetchProjectsSummaryReducer = {
  [`${actionTypes.FETCH_CATEGORIES_SUMMARY}_PENDING`]: (state: ProjectsV2Store, action: ReduxActionResponse<ProjectsSummaryResponse>) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchCategoriesSummary = getPendingStatus();
      draftState.asyncStatus.fetchCategoriesSummary.timestamp = action.meta.timestamp;
    }),
  [`${actionTypes.FETCH_CATEGORIES_SUMMARY}_FULFILLED`]: (state: ProjectsV2Store, action: ReduxActionResponse<ProjectsSummaryResponse>) =>
    produce(state, draftState => {
      if (draftState.asyncStatus.fetchCategoriesSummary.timestamp === action.meta.timestamp) {
        draftState.asyncStatus.fetchCategoriesSummary = getInitialStatus();
        draftState.categoriesSummary = action.payload.data?.summary ?? null;
      }
    }),
  [`${actionTypes.FETCH_CATEGORIES_SUMMARY}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchCategoriesSummary = getErrorStatus(action.payload);
    }),
};

// Reducers for FETCH_ORGANIZATION_LABELS actions
const fetchOrganzationLabelsReducer = {
  [`${actionTypes.FETCH_ORGANIZATION_LABELS}_PENDING`]: (state: ProjectsV2Store, action: ReduxActionResponse<LabelsResponse>) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchOrganizationLabels = getPendingStatus();
      draftState.asyncStatus.fetchOrganizationLabels.timestamp = action.meta.timestamp;
    }),
  [`${actionTypes.FETCH_ORGANIZATION_LABELS}_FULFILLED`]: (state: ProjectsV2Store, action: ReduxActionResponse<LabelsResponse>) =>
    produce(state, draftState => {
      if (draftState.asyncStatus.fetchOrganizationLabels.timestamp === action.meta.timestamp) {
        draftState.asyncStatus.fetchOrganizationLabels = getInitialStatus();
        draftState.organizationLabels = action.payload.data?.labels ?? [];
      }
    }),
  [`${actionTypes.FETCH_ORGANIZATION_LABELS}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchOrganizationLabels = getErrorStatus(action.payload);
    }),
};

const setCategoryReducer = {
  [actionTypes.SET_CATEGORY]: (state: ProjectsV2Store, action: SyncReduxAction<{ category: ProjectCategory }>) =>
    produce(state, draftState => {
      //  If the category is being updated, we need to reset the categorySummary so it will be re-fetched.
      if (!_.isEqual(draftState.category, action.category)) {
        draftState.selectedProjects = [];
      }
      draftState.category = action.category;
    }),
};

const deleteProjectsReducer = {
  [`${actionTypes.DELETE_PROJECTS}_PENDING`]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.updateProjects = getPendingStatus();
    }),
  [`${actionTypes.DELETE_PROJECTS}_FULFILLED`]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.updateProjects = getInitialStatus();
    }),
  [`${actionTypes.DELETE_PROJECTS}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.updateProjects = getErrorStatus(action.payload);
    }),
};

const removeTeamReducer = {
  [`${actionTypes.REMOVE_FROM_TEAM}_PENDING`]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.removeTeam = getPendingStatus();

      // We reset here so that the selection gets cleared after we've actually sent the API request.
      draftState.selectedProjects = [];
    }),
  [`${actionTypes.REMOVE_FROM_TEAM}_FULFILLED`]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.removeTeam = getInitialStatus();
    }),
  [`${actionTypes.REMOVE_FROM_TEAM}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.removeTeam = getErrorStatus(action.payload);
    }),
};

const setFilterReducer = {
  [actionTypes.SET_FILTER]: (state: ProjectsV2Store, action: SyncReduxAction<{ filter: AnyFilter }>) =>
    produce(state, draftState => {
      resetPageAndSelectedProjects(draftState);
      draftState.filter = action.filter;
      draftState.asyncStatus.fetchProjects = getPendingStatus();
    }),
  [actionTypes.RESET_FILTERS]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      resetPageAndSelectedProjects(draftState);
      draftState.title = initialState.title;
      draftState.filter = initialState.filter;
    }),
};

const selectFilteredProjectsReducer = {
  [`${actionTypes.SELECT_ALL_FILTERED_PROJECTS}_PENDING`]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchProjects = getPendingStatus();

      // We reset here so that the selection gets cleared after we've actually sent the API request.
      draftState.selectedProjects = [];
    }),
  [`${actionTypes.SELECT_ALL_FILTERED_PROJECTS}_FULFILLED`]: (state: ProjectsV2Store, action: ReduxActionResponse<ProjectsResponse>) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchProjects = getInitialStatus();
      draftState.selectedProjects = action.payload.data?.projects || [];
    }),
  [`${actionTypes.SELECT_ALL_FILTERED_PROJECTS}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.fetchProjects = getErrorStatus(action.payload);
    }),
};

const bulkApplyLabelReducer = {
  [`${actionTypes.BULK_APPLY_LABEL}_PENDING`]: (state: ProjectsV2Store) =>
    produce(state, draftState => {
      draftState.asyncStatus.bulkApplyLabel = getPendingStatus();
      draftState.bulkLabelData = {
        created: 0,
        errors: {},
      };
    }),
  [`${actionTypes.BULK_APPLY_LABEL}_FULFILLED`]: (state: ProjectsV2Store, action: ReduxActionResponse<BulkAddLabelResponse>) =>
    produce(state, draftState => {
      draftState.asyncStatus.bulkApplyLabel = getInitialStatus();
      draftState.bulkLabelData.created = action.payload.data?.created ?? 0;
      draftState.bulkLabelData.errors = action.payload.data?.errors ?? {};
    }),
  [`${actionTypes.BULK_APPLY_LABEL}_REJECTED`]: (state: ProjectsV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.bulkApplyLabel = getErrorStatus(action.payload);
    }),
};

export default createReducer<ProjectsV2Store>(initialState, {
  ...resetStateReducer,
  ...preFetchLoadingReducer,
  ...setSortReducer,
  ...setTitleReducer,
  ...paginationReducer,
  ...hydrateStateReducer,
  ...fetchProjectsReducers,
  ...selectProjectsReducer,
  ...fetchProjectsSummaryReducer,
  ...fetchOrganzationLabelsReducer,
  ...deleteProjectsReducer,
  ...setCategoryReducer,
  ...setFilterReducer,
  ...removeTeamReducer,
  ...selectFilteredProjectsReducer,
  ...bulkApplyLabelReducer,
});
